/**
 * InvoiceReviewSend — Full-page flow for creating and sending an invoice.
 *
 * F5 spec:
 * - Route: /jobs/:jobId/invoices/new (creation only from job context)
 * - Live invoice document preview (left) + controls (right)
 * - Recipients: customer contacts multi-select, primary pre-checked, inline add new
 * - Display toggles: line-item pricing, labor pricing, note, attachments
 * - Terms LOCKED (changing requires customer approval, no inline edit)
 * - Note (prints on invoice)
 * - Attachments from: this job's files, Documents repo, new upload
 * - Footer: Cancel · Download PDF · Send Invoice
 * - On send → returns to job detail
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import { Button } from '../ui/Button';
import { AttachModal } from '../attachments/AttachModal';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Checkbox } from '../ui/Checkbox';
import DetailPageHeader from '../ui/DetailPageHeader';
import { useToast } from '../ui/Toast';
import { useConfirm } from '../ui/ConfirmationModal';
import { invoiceAPI, jobAPI, customerAPI, quoteAPI, api as apiClient } from '../../services/api';
import { invoiceFilesFromJob } from './invoiceFiles';
import useBranding from '../../hooks/useBranding';
import { formatInvoiceNumberFor } from '../../utils/displayId';
import { deriveDueDate, buildSplitPreview, withDueOnReceipt, termValueFor, termStoredName, jobHeaderLabel, acceptedDeliveries, failedDeliveries, invoiceSentToast, invoicePartialToast, splitSentToast, invoiceNotSentToast, DUE_ON_RECEIPT } from './invoiceLifecycle';
import SendComposerModal, { defaultInvoiceSubject, withInvoiceNumber } from '../shared/SendComposerModal';
import DeliveryList from './DeliveryList';
import { dealerToday } from '../../utils/dealerDate';
import { parseDate } from '../../utils/formatDate';
import SelectMenu from '../ui/SelectMenu';
import { useSettingsContext } from '../../hooks/useSettings';
import useSettingsList from '../../hooks/useSettingsList';
import { DocumentHeader } from '../quotes/QuoteDocument';
import useCompanyProfile from '../../hooks/useCompanyProfile';
import { seedRecipientsFromContacts } from '../shared/ReviewAndSendShell';

/* ─── helpers ─────────────────────────────────────────────────────── */
const TYPED_EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

/**
 * IDI-485 (Q-16): the ticked recipients as {email, name}, trimmed and
 * de-duped on the lower-cased address. The server re-validates and de-dupes.
 */
export function sendRecipients(recipients, selectedIds) {
  const seen = new Set();
  const out = [];
  for (const c of recipients || []) {
    if (!(selectedIds || []).includes(c.id)) continue;
    const email = String(c.email || '').trim();
    const key = email.toLowerCase();
    if (!email || seen.has(key)) continue;
    seen.add(key);
    out.push({ email, name: c.name && c.name !== email ? c.name : '' });
  }
  return out;
}

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

// IDI-53: job line items carry money/quantity under different field names
// depending on how the job was created -- {unit_rate, line_total} (quote-
// sourced), {unitPrice, amount} (legacy/simple), or {lineTotal,
// materialCostCents, laborCostCents, itemCostCents} (job/WO-embedded, no
// per-unit rate at all). This mirrors cmd/invoices' getLineItemMoney (Go) so
// the create-preview table and the backend-computed subtotal agree, instead
// of the preview reading item.qty/item.rate/item.total -- field names that
// don't exist on ANY of the real schemas -- and rendering four blank rows
// against a real $0.00 total (the exact reported defect).
function normalizeLineItem(li) {
  const name = li.name || li.item_name || li.itemName || li.description || '';
  const qty = Number(li.quantity ?? li.qty ?? 1) || 1;
  let rate = Number(li.unit_rate ?? li.unitPrice ?? NaN);
  let total = Number(li.line_total ?? li.lineTotal ?? li.amount ?? NaN);
  if (!Number.isFinite(total)) total = 0;
  if (!Number.isFinite(rate)) {
    // Schema 3 (lineTotal + cost components, no rate) -- derive a display
    // rate from quantity, same fallback the backend uses.
    rate = qty > 0 ? Math.round(total / qty) : 0;
  }
  return { ...li, name, qty, rate, total };
}
export { normalizeLineItem };

// IDI-193: apiClient.post() (services/api.js) already unwraps the backend's
// {success, data} envelope, so createInvoice's resolved value IS the invoice
// object -- callers must NOT also read `.data.id` off it (that's always
// undefined). This mirrors the shape createInvoice's callers have to defend
// against, extracted so the exact bug (id resolves to undefined -> sendInvoice
// is silently never called -> a false "sent" toast) is directly testable
// without mounting the whole async send flow.
function resolveCreatedInvoiceId(createInvoiceResult) {
  return createInvoiceResult?.data?.id || createInvoiceResult?.id || null;
}
export { resolveCreatedInvoiceId };

// IDI-817 D6: a bare YYYY-MM-DD is the dealer's day; parseDate reads it as
// that local day (not UTC midnight, the day before in a US browser).
function formatDate(iso) {
  const d = parseDate(iso);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// IDI-817 D3: Review & Send is a draft's. Anything already sent (or paid)
// opens its own page instead.
function isSentOrPaid(inv) {
  const s = String(inv?.status || '').toLowerCase();
  return s === 'sent' || s === 'paid' || s === 'past_due' || s === 'overdue';
}

// IDI-450/481/817: the server's preview answer, as the page reads it. A saved
// invoice's preview (IDI-817 D1) carries the same fields as a new one's.
function pricingFromPreview(p) {
  return {
    subtotal: p?.subtotal ?? 0,
    discount: p?.discountAmount ?? 0,
    tax: p?.taxAmount ?? 0,
    total: p?.totalAmount ?? 0,
    appliedChangeOrderIds: p?.appliedChangeOrderIds || [],
    changeOrderNetCents: p?.changeOrderNetCents || 0,
    lineItems: p?.lineItems || null,
    jobTotal: p?.jobTotalCents ?? null,
    invoicedToDate: p?.invoicedToDateCents ?? 0,
    remaining: p?.remainingCents ?? null,
    overInvoiced: p?.overInvoicedCents ?? 0,
  };
}

/* ─── component ───────────────────────────────────────────────────── */
// IDI-554: the server-resolved Bill To, laid out like QuoteDocument's.
export function BillToBlock({ billTo }) {
  if (!billTo) return null;
  const csz = [billTo.city, billTo.state, billTo.zip].filter(Boolean).join(' ');
  const lines = [billTo.contactName, billTo.street, billTo.street2, csz, billTo.contactPhone].filter(Boolean);
  if (!billTo.name && lines.length === 0) return null;
  return (
    <div className="border-b border-border-default pb-4 mb-4" data-testid="invoice-bill-to">
      <p className="text-sm text-content-secondary">Bill To</p>
      {billTo.name && <p className="text-base font-medium text-content-primary" data-testid="bill-to-line">{billTo.name}</p>}
      {lines.map((line, i) => (
        <p key={i} className="text-sm text-content-primary" data-testid="bill-to-line">{line}</p>
      ))}
    </div>
  );
}

export default function InvoiceReviewSend() {
  // PT-34.3 (2026-07-28): dual-mode. When routed via /invoices/:invoiceId/review,
  // load an existing invoice (no creation). When via /jobs/:jobId/invoices/new,
  // create mode (existing behavior). The `invoiceId` param presence is the
  // discriminator. Spec rule: do NOT create a duplicate draft on entry.
  const { jobId, invoiceId } = useParams();
  const isExistingInvoice = !!invoiceId;
  const navigate = useNavigate();
  // IDI-817: the load effect redirects a sent invoice; read navigate through
  // a ref so it never joins that effect's deps (a test's useNavigate mock is
  // a new function each render, IDI-714).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // PR-02e: canonical Toast API is { show, success, info, warning, error }.
  const toast = useToast();
  const confirm = useConfirm();

  /* ── canonical settings ── */
  // PT-23 (2026-07-26): `INVOICE_TERMS` local shadow deleted — payment terms
  // now sourced from the canonical `payment_term.v1` seed via `useSettings`.
  // `deriveDueDate` (Shape A) reads `behavior.offset_days` from the same list.
  const { getEnumLabel, quoteDefaults } = useSettingsContext();
  // IDI-451: the dealer's default term (Settings → Quote Defaults).
  const quoteDefaultPaymentTerms = quoteDefaults?.payment_terms || quoteDefaults?.paymentTerms || '';
  // Set once the job is read and no quote term was found.
  const [termsResolved, setTermsResolved] = useState(false);
  const { profile: companyProfile } = useCompanyProfile();
  // IDI-451: the RAW term rows (getOptions keeps only value/label, dropping
  // the `days`/`behavior` a due date needs), plus the built-in Due on receipt.
  const { items: termRows } = useSettingsList('payment_terms');
  const paymentTermsOptions = useMemo(() => withDueOnReceipt(termRows), [termRows]);

  /* ── state ── */
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Invoice data
  const [job, setJob] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [invoiceData, setInvoiceData] = useState({
    terms: '',
    // IDI-538: the dealer's (Central) day, not UTC's.
    invoicedDate: dealerToday(),
    note: '',
    amount: 0,
  });

  // IDI-451: a split is independent of the payment term — a Net 15 job can be
  // billed 30/70. The old UI only split when terms were exactly '50_50', and
  // always 50/50.
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [depositPercent, setDepositPercent] = useState(50);
  // IDI-451 (1): the field keeps what's typed ("3" on the way to "30") and
  // clamps only on blur; clamping per keystroke turned 30 into 99.
  const [depositText, setDepositText] = useState('50');
  // IDI-450: the server prices the invoice (snapshot + applied change
  // orders). The preview shows what will actually be billed.
  const [pricing, setPricing] = useState(null);
  // IDI-554: the Bill To, resolved by the server (one Go resolver for the
  // preview, the saved invoice and the PDF). Rendered as returned; no JS
  // resolution.
  const [billTo, setBillTo] = useState(null);
  const [pricingError, setPricingError] = useState(null);

  // Display toggles
  const [showLineItemPricing, setShowLineItemPricing] = useState(true);
  const [showLaborPricing, setShowLaborPricing] = useState(true);
  const [showNote, setShowNote] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  // Recipients
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  // IDI-485: a typed (non-contact) recipient, and the composer.
  const [typedEmail, setTypedEmail] = useState('');
  const [typedError, setTypedError] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  // IDI-485 review fix 2: once Send has created the invoice (or split), a
  // failed send must never create another: a retry sends THIS one. Holds
  // { id, number, completionNumber } of what was created.
  const createdRef = useRef(null);
  // The job's quote: its salespersons/assignee seed the CC row (IDI-548).
  const [ccQuote, setCcQuote] = useState(null);

  // Attachments. IDI-484: AttachmentRefs ({attachmentId, filename, ...})
  // stored on the job with sourceType="invoice".
  const [attachments, setAttachments] = useState([]);
  // §2 (2026-08-08): B2 enabled; wire the previously-stubbed Attach button.
  const [attachOpen, setAttachOpen] = useState(false);
  // IDI-484: the job the files are stored on (route param on a new invoice,
  // the invoice's job on an existing one).
  const jobIdForFiles = jobId || job?.id || null;

  // Removing an invoice file deletes it: it lives on the job, so un-listing
  // it here would leave it in the job's Files tab with an invoice label.
  const handleRemoveAttachment = useCallback(async (a) => {
    const ok = await confirm({
      title: 'Delete this file?',
      message: `"${a.filename}" will be deleted from this invoice and from the job's Files.`,
      confirmLabel: 'Delete file',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.deleteAttachment({ attachmentId: a.attachmentId });
      setAttachments((prev) => prev.filter((x) => x.attachmentId !== a.attachmentId));
    } catch (err) {
      toast.error(err?.response?.status === 409 ? 'This job was changed by someone else. Reload and try again.' : 'Could not delete the file.');
    }
  }, [confirm, toast]);

  // PR-36 §1.4 (2026-08-17): the branding fetch that lived here (13 lines of
  // useState + useEffect + silent catch, BR-1 consumer #6 by count) is now
  // one line. All five review-and-send + document surfaces share it.
  const { branding } = useBranding();
  // IDI-485: the dealer's name in the composer's default subject.
  const dealerName = companyProfile?.company_name || branding?.company_name || '';

  /* ── load data (dual-mode) ── */
  useEffect(() => {
    let cancelled = false;

    async function loadExistingInvoice() {
      // PT-34.3: load an existing invoice by ID. No draft creation.
      try {
        const res = await invoiceAPI.getInvoice(invoiceId);
        const inv = res?.data || res;
        // IDI-817 D3: Review & Send is a draft's; a sent or paid invoice's
        // /review opens its page (Send Again lives there, IDI-580).
        if (!cancelled && inv && isSentOrPaid(inv)) {
          navigateRef.current(`/invoices/${invoiceId}`, { replace: true });
          return;
        }
        if (!cancelled && inv) {
          setJob({ id: inv.job_id || inv.jobId, name: inv.job_title || '' });
          setBillTo(inv.billTo || null);
          // IDI-484 (Q-45): this invoice's files are stored on its job.
          const invJobId = inv.job_id || inv.jobId;
          if (invJobId) {
            try {
              const jr = await jobAPI.getJob(invJobId);
              const jd = jr?.data || jr;
              // IDI-538: the header's J-number · title come from the job.
              if (!cancelled && jd) setJob((prev) => ({ ...prev, ...jd, id: invJobId }));
              if (!cancelled) setAttachments(invoiceFilesFromJob(jd?.documents, invoiceId));
            } catch { /* the invoice still loads; its file list stays empty */ }
          }
          setLineItems((inv.line_items || inv.lineItems || []).map(normalizeLineItem));
          // IDI-483: saved recipients go through the shared seeder too (one
          // row per email, a unique key per row); all of them stay ticked.
          let contacts = seedRecipientsFromContacts(inv.recipients || []).map((c) => ({ ...c, checked: true }));
          // IDI-538: an invoice stores no recipients; seed from the
          // customer's contacts, as a new invoice does.
          const invCid = inv.customerId || inv.customer_id;
          if (contacts.length === 0 && invCid) {
            try {
              const cust = await customerAPI.getCustomer(invCid);
              const c = cust?.data || cust || {};
              contacts = seedRecipientsFromContacts(
                Array.isArray(c.contacts) ? c.contacts : [],
                { email: c.email || inv.customerEmail || inv.customer_email || '', name: c.name || inv.customerName || inv.customer_name || '' },
              );
            } catch { /* recipients can still be added by hand */ }
          }
          if (cancelled) return;
          setRecipients(contacts);
          setSelectedRecipients(contacts.filter((c) => c.checked).map((c) => c.id));
          // IDI-485: the last send's per-recipient outcomes (a failed send
          // leaves the draft here with its failures).
          if (Array.isArray(inv.deliveries) && inv.deliveries.length) setSendResult(inv);
          setInvoiceData((prev) => ({
            ...prev,
            // IDI-817: the record stores `totalAmount` (cents), not `total`.
            amount: inv.totalAmount ?? inv.total ?? inv.amount ?? 0,
            terms: inv.terms || inv.payment_terms || 'net_30',
            id: inv.id || invoiceId,
            invoiceNumber: inv.invoice_number || inv.invoiceNumber,
            jobNumber: inv.jobNumber || inv.job_number,
            ...(inv.invoicedDate ? { invoicedDate: inv.invoicedDate } : {}),
          }));
          // IDI-817 D1: what Send bills now (a completion draft recomputed
          // to what's left), unwritten, with Already invoiced / Remaining
          // over the job's other invoices.
          try {
            const pr = await invoiceAPI.previewInvoice({ invoice_id: invoiceId });
            const p = pr?.data || pr;
            if (!cancelled && p) {
              setPricing(pricingFromPreview(p));
              const pl = p.lineItems || p.line_items;
              if (Array.isArray(pl) && pl.length) setLineItems(pl.map(normalizeLineItem));
              setPricingError(null);
            }
          } catch (err) {
            if (!cancelled) setPricingError(err?.message || 'Could not price this invoice.');
          }
          if (cancelled) return;
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          toast.error('Failed to load invoice');
        }
      }
    }

    async function loadJob() {
      try {
        const res = await jobAPI.getJob(jobId);
        const data = res?.data || res;
        if (!cancelled) {
          setJob(data || { id: jobId });
          // IDI-53: normalize field names (see normalizeLineItem) so the
          // preview table shows real qty/rate/total instead of four blank
          // rows, and so this SAME total feeds invoiceData.amount —
          // previously computed from `li.total` (a field that doesn't exist
          // on any real job line-item schema), always $0.
          const rawItems = data?.line_items || data?.lineItems || [];
          const items = rawItems.map(normalizeLineItem);
          setLineItems(items);
          const total = items.reduce((sum, li) => sum + (li.total || 0), 0);
          // IDI-451 (Adrian 10-14): the job's terms are its QUOTE's term
          // (`payment_term` on app quotes, `paymentTermId` on migrated ones),
          // else the dealer's default (quote defaults' payment_terms).
          let jobTerms = data?.terms || data?.payment_terms || '';
          if (!jobTerms && (data?.quoteId || data?.quote_id)) {
            try {
              const q = await quoteAPI.getQuote(data.quoteId || data.quote_id);
              const qq = q?.quote || q?.data?.quote || q?.data || q || {};
              jobTerms = qq.payment_term || qq.payment_terms || qq.paymentTermId || qq.paymentTerms || '';
            } catch { /* fall back to the dealer default */ }
          }
          // No quote term: leave it empty here; the effect below fills in
          // the dealer default once Settings has loaded.
          setInvoiceData((prev) => ({ ...prev, amount: total, terms: jobTerms }));
          setTermsResolved(true);

          // IDI-53: recipients were read from `data.contacts` /
          // `data.customer.contacts` — fields that don't exist on the job
          // record at all (jobs carry `customerId`, not an embedded
          // customer/contacts object), so this was ALWAYS an empty array
          // (the reported "four blank rows" also covered this — zero
          // recipient checkboxes ever rendered). Same fix as
          // QuoteReviewSend.js (PT-46 46.4c): resolve the CUSTOMER record by
          // id and use the shared seedRecipientsFromContacts so Quote / CO /
          // Invoice R&S surfaces stay consistent.
          const cid = data?.customerId || data?.customer_id;
          let custRecord = null;
          if (cid) {
            try {
              custRecord = await customerAPI.getCustomer(cid);
            } catch {
              // Non-fatal: fall through to seedRecipientsFromContacts' own
              // empty-input handling; the user can still add recipients
              // manually via the composer.
            }
          }
          const customerContacts = Array.isArray(custRecord?.contacts) ? custRecord.contacts : [];
          const seeded = seedRecipientsFromContacts(
            customerContacts,
            { email: custRecord?.email || data?.customer_email || '', name: custRecord?.name || data?.customerName || data?.customer_name || '' },
          );
          setRecipients(seeded);
          setSelectedRecipients(seeded.filter((c) => c.checked).map((c) => c.id));

          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
          toast.error('Failed to load job data');
        }
      }
    }

    if (isExistingInvoice) {
      loadExistingInvoice();
    } else {
      loadJob();
    }
    return () => { cancelled = true; };
  }, [jobId, invoiceId, isExistingInvoice, toast]);

  /* ── computed ── */
  const dueDate = deriveDueDate(invoiceData.invoicedDate, invoiceData.terms, paymentTermsOptions);
  const termsLabel = getEnumLabel('payment_terms', invoiceData.terms) || invoiceData.terms;

  // PT-26 / IDI-451: a 50/50 payment term still implies a split, and the
  // toggle enables one for any term.
  const isSplit = !isExistingInvoice && (splitEnabled || invoiceData.terms === '50_50');
  // IDI-450: bill what the server says, which includes applied change orders.
  const billedAmount = pricing?.total ?? invoiceData.amount;
  // IDI-481 (Q-13): nothing left to bill -> no new invoice.
  const nothingLeft = pricing != null && pricing.remaining != null && pricing.remaining <= 0;
  // IDI-451 (4): a split divides what's LEFT to bill (B-10), as the server
  // does; the deposit is due on receipt (U-22).
  const splitBase = pricing && pricing.remaining != null ? Math.max(0, pricing.remaining) : billedAmount;
  // IDI-516 (5): show the cents Send will store. The server rounds the
  // subtotal and tax separately; re-deriving from the total put the odd cent
  // on the wrong half at some percentages. Ask it (create_split, preview),
  // falling back to the local estimate until it answers.
  const [serverSplit, setServerSplit] = useState(null);
  useEffect(() => {
    if (!isSplit || isExistingInvoice || !jobId) { setServerSplit(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      invoiceAPI.createSplit({ job_id: jobId, deposit_percent: depositPercent, preview: true })
        .then((r) => { if (!cancelled) setServerSplit(r?.data || r); })
        .catch(() => { if (!cancelled) setServerSplit(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isSplit, isExistingInvoice, jobId, depositPercent, pricing?.remaining]);
  const splitInvoices = useMemo(() => {
    if (!isSplit) return null;
    const local = buildSplitPreview(splitBase, depositPercent, invoiceData.invoicedDate, invoiceData.invoicedDate);
    return serverSplit?.deposit && serverSplit?.completion
      ? [{ ...local[0], amount: serverSplit.deposit.totalAmount }, { ...local[1], amount: serverSplit.completion.totalAmount }]
      : local;
  }, [isSplit, splitBase, depositPercent, invoiceData.invoicedDate, serverSplit]);

  // IDI-450: ask the server what this invoice costs. One pricing
  // implementation, so the preview cannot drift from the saved invoice.
  useEffect(() => {
    if (isExistingInvoice || !jobId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await invoiceAPI.previewInvoice({ job_id: jobId });
        const p = res?.data || res;
        if (!cancelled) {
          setBillTo(p?.billTo || null);
          setPricing({
            subtotal: p?.subtotal ?? 0,
            discount: p?.discountAmount ?? 0,
            tax: p?.taxAmount ?? 0,
            total: p?.totalAmount ?? 0,
            appliedChangeOrderIds: p?.appliedChangeOrderIds || [],
            changeOrderNetCents: p?.changeOrderNetCents || 0,
            lineItems: p?.lineItems || null,
            // IDI-481: what's left to bill (job total minus every non-void
            // invoice, drafts included). The server also refuses more.
            jobTotal: p?.jobTotalCents ?? null,
            invoicedToDate: p?.invoicedToDateCents ?? 0,
            remaining: p?.remainingCents ?? null,
            overInvoiced: p?.overInvoicedCents ?? 0,
          });
          setPricingError(null);
        }
      } catch (err) {
        // Never silently fall back to a job-only total: that is the IDI-450
        // under-billing. Say so instead.
        if (!cancelled) setPricingError(err?.message || 'Could not price this invoice.');
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, isExistingInvoice]);

  // IDI-451: no quote term -> the dealer's default (Quote Defaults), never a
  // blank picker. Runs after the job read so it never overrides a real term.
  useEffect(() => {
    if (!termsResolved || isExistingInvoice) return;
    setInvoiceData((prev) => (prev.terms ? prev : { ...prev, terms: quoteDefaultPaymentTerms || 'net_30' }));
  }, [termsResolved, isExistingInvoice, quoteDefaultPaymentTerms]);

  /* ── handlers ── */
  // IDI-485: the CC row's salesperson seed is the job's quote (IDI-548):
  // picked salespersons[] → the assignee → none. Never the creator.
  const ccQuoteId = job?.quoteId || job?.quote_id;
  useEffect(() => {
    if (!ccQuoteId || typeof quoteAPI?.getQuote !== 'function') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = await quoteAPI.getQuote(ccQuoteId);
        const qq = q?.quote || q?.data?.quote || q?.data || q || null;
        if (!cancelled) setCcQuote(qq);
      } catch { /* the CC row still seeds the signed-in user */ }
    })();
    return () => { cancelled = true; };
  }, [ccQuoteId]);

  // IDI-485: add a typed (non-contact) recipient, ticked.
  const addTypedRecipient = useCallback(() => {
    const email = typedEmail.trim();
    if (!TYPED_EMAIL_RE.test(email)) {
      setTypedError('Enter a valid email address.');
      return;
    }
    const existing = recipients.find((c) => String(c.email || '').trim().toLowerCase() === email.toLowerCase());
    if (existing) {
      setSelectedRecipients((prev) => (prev.includes(existing.id) ? prev : [...prev, existing.id]));
    } else {
      const id = `typed-${email.toLowerCase()}`;
      setRecipients((prev) => [...prev, { id, email, name: email, checked: true }]);
      setSelectedRecipients((prev) => [...prev, id]);
    }
    setTypedEmail('');
    setTypedError('');
  }, [typedEmail, recipients]);

  // IDI-485: the composer's values ride on every send path.
  const doSend = useCallback(async (composer = {}) => {
    setSending(true);
    const toList = sendRecipients(recipients, selectedRecipients);
    const emailPayload = {
      recipients: toList,
      subject: composer.subject || '',
      message: composer.message || '',
      ...(composer.cc && composer.cc.length ? { cc: composer.cc } : {}),
    };
    // A send that reached nobody leaves the invoice a draft (Q-14): show
    // each failure. A just-created draft is reopened on its own Review &
    // Send page, so a retry sends it rather than creating another.
    const reportNotSent = (inv, number) => {
      setSendResult(inv);
      setComposerOpen(false);
      toast.error(invoiceNotSentToast(number, inv));
      const id = inv?.id || createdRef.current?.id;
      if (!isExistingInvoice && id) navigate(`/invoices/${id}/review`, { replace: true });
    };
    // Sends the invoice create mode already made (split deposit or single).
    // Review fix 1: the subject confirmed before the invoice existed ("Invoice
    // from <Dealer>") goes out with its real number once it does.
    const sendCreated = async (created) => {
      const subject = created.number
        ? withInvoiceNumber(emailPayload.subject || defaultInvoiceSubject(null, dealerName), created.number)
        : emailPayload.subject;
      const sentRes = await invoiceAPI.sendInvoice(created.id, { ...emailPayload, subject });
      const sentInv = sentRes?.data || sentRes;
      const num = sentInv?.invoiceNumber || created.number;
      if (acceptedDeliveries(sentInv).length === 0) {
        reportNotSent({ id: created.id, ...sentInv }, num);
        return false;
      }
      // IDI-596 (D3): some failed → a warning naming them (split too).
      if (failedDeliveries(sentInv).length > 0) toast.warning(invoicePartialToast(num, sentInv));
      else toast.success(created.completionNumber !== undefined
        ? splitSentToast(num, created.completionNumber)
        : invoiceSentToast(num, sentInv));
      return true;
    };
    try {
      // IDI-538: an existing draft is SENT, not re-created (createInvoice
      // 400s "Job ID is required" without a job route param).
      if (isExistingInvoice) {
        // IDI-519 (B-11): the draft's terms stay editable until sent; the Send
        // carries them (no separate PUT) and the server dates it and sets
        // its due date from them.
        // Always send the page's current selection; a blank picker falls back
        // to the dealer default rather than silently keeping the old terms.
        const res = await invoiceAPI.sendInvoice(invoiceId, { ...emailPayload, terms: invoiceData.terms || quoteDefaultPaymentTerms || 'net_30' });
        const sent = res?.data || res;
        const num = sent?.invoiceNumber || sent?.invoice_number || invoiceData.invoiceNumber;
        if (acceptedDeliveries(sent).length === 0) {
          reportNotSent(sent, num);
          return;
        }
        if (failedDeliveries(sent).length > 0) toast.warning(invoicePartialToast(num, sent));
        else toast.success(invoiceSentToast(num, sent));
        const backJob = job?.id;
        // IDI-566: ?tab= (not nav state) — JobDetail reads it, and it survives a reload.
        navigate(backJob ? `/jobs/${backJob}?tab=invoices` : `/invoices/${invoiceId}`);
        return;
      }
      const basePayload = {
        job_id: jobId,
        terms: invoiceData.terms,
        invoiced_date: invoiceData.invoicedDate,
        note: showNote ? invoiceData.note : '',
        recipients: selectedRecipients,
        display: {
          line_item_pricing: showLineItemPricing,
          labor_pricing: showLaborPricing,
          note: showNote,
          attachments: showAttachments,
        },
        // IDI-484: files attached while creating are on the job, unclaimed;
        // the server tags them with the new invoice's id.
        attachments: attachments.map((a) => a.attachmentId).filter(Boolean),
      };

      if (createdRef.current) {
        // A retry after a failed send: the invoice already exists.
        if (!(await sendCreated(createdRef.current))) return;
      } else if (isSplit && splitInvoices) {
        // IDI-451 (3) / D-18: ONE request creates both halves, linked, in a
        // single transaction, sized from what's left to bill. The deposit is
        // sent now; the completion stays a DRAFT (B-11) and is billed for
        // whatever's left when it is sent.
        const res = await invoiceAPI.createSplit({ ...basePayload, deposit_percent: depositPercent, due_date: dueDate || null });
        const out = res?.data || res;
        const depId = out?.deposit?.id;
        if (!depId) throw new Error('split not created');
        createdRef.current = { id: depId, number: out?.deposit?.invoiceNumber, completionNumber: out?.completion?.invoiceNumber || null };
        if (!(await sendCreated(createdRef.current))) return;
      } else {
        // Standard single invoice
        // IDI-519: save the due date this page shows (as the split does).
        const payload = { ...basePayload, amount: invoiceData.amount, due_date: dueDate || null };
        const result = await invoiceAPI.createInvoice(payload);
        // IDI-193: createInvoice's response is already unwrapped by
        // apiClient.post (see services/api.js post()) -- it returns the
        // invoice object directly, so result.data is undefined and
        // result.data.id was ALWAYS falsy here. sendInvoice was silently
        // never called for a standard (non-split) invoice: it was created as
        // a draft, the status-flip PUT never fired, yet the toast still said
        // "Invoice sent successfully". The split-invoice branch above already
        // had the correct `?.data?.id || ?.id` fallback; this branch was
        // missing it.
        const invoiceId = resolveCreatedInvoiceId(result);
        if (invoiceId) {
          createdRef.current = { id: invoiceId, number: result?.invoiceNumber || result?.data?.invoiceNumber };
          if (!(await sendCreated(createdRef.current))) return;
        } else {
          // Defensive: if the id truly can't be resolved, don't claim success
          // for a send that never happened — this exact silent-failure shape
          // is the bug being fixed here.
          toast.error('Invoice created but could not be sent — please check the invoice list.');
        }
      }
      setComposerOpen(false);
      navigate(`/jobs/${jobId}?tab=invoices`); // IDI-566
    } catch (err) {
      // IDI-481: the server refuses more than what's left to bill; say why.
      const body = err?.response?.data;
      if (body?.error === 'over_remaining') {
        toast.error(body.message || 'This invoice is more than what is left to bill on the job.');
      } else if (err?.response?.status === 400 && body?.message) {
        toast.error(body.message);
      } else {
        toast.error('Failed to send invoice');
      }
      // Review fix 2: the invoice exists now; stay on THAT draft (never the
      // create form), so a retry sends it instead of creating another.
      const created = createdRef.current;
      if (!isExistingInvoice && created?.id) {
        setComposerOpen(false);
        navigate(`/invoices/${created.id}/review`, { replace: true });
      }
    } finally {
      setSending(false);
    }
  }, [
    jobId, invoiceData, selectedRecipients, recipients, showLineItemPricing,
    showLaborPricing, showNote, showAttachments, attachments,
    navigate, toast, isSplit, splitInvoices, depositPercent, dueDate,
    isExistingInvoice, invoiceId, job?.id, quoteDefaultPaymentTerms, dealerName,
  ]);

  // PT-25 (25.9) → IDI-485: the composer IS the send confirm. Nothing goes
  // out until it is confirmed.
  const handleSend = useCallback(() => {
    setSendResult(null);
    setComposerOpen(true);
  }, []);
  const composerTo = sendRecipients(recipients, selectedRecipients).map((r) => r.email).join(', ');

  const handleDownloadPDF = useCallback(async () => {
    setDownloading(true);
    try {
      toast.info('Generating PDF…');
      // IDI-562: an existing draft renders ITSELF (invoice_id); this route
      // has no job param, so the preview's job_id was undefined and 400'd.
      // IDI-511: in create mode, a PREVIEW. The server prices the page's
      // current values and renders the PDF without creating an invoice or
      // using a number.
      const pdfRes = await invoiceAPI.previewPDF({
        ...(isExistingInvoice ? { invoice_id: invoiceId } : { job_id: jobId }),
        terms: invoiceData.terms,
        invoiced_date: invoiceData.invoicedDate,
        due_date: dueDate || undefined,
        note: showNote ? invoiceData.note : '',
        display: {
          line_item_pricing: showLineItemPricing,
          labor_pricing: showLaborPricing,
          note: showNote,
          attachments: showAttachments,
        },
      });
      const pdfData = pdfRes?.data || pdfRes;
      const pdfBase64 = pdfData?.pdf || pdfData;
      if (!pdfBase64 || typeof pdfBase64 !== 'string') {
        toast.error('No PDF data received');
        return;
      }
      // IDI-519: the first download dates the draft; show that date.
      if (isExistingInvoice && pdfData?.invoicedDate) {
        setInvoiceData((prev) => ({ ...prev, invoicedDate: pdfData.invoicedDate }));
      }
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      // IDI-516 (3): name the file after the invoice, not "invoice-preview".
      const num = invoiceData?.invoiceNumber || (invoiceData?.id ? formatInvoiceNumberFor(invoiceData) : '');
      a.download = num && num !== '—' ? `Invoice-${num}.pdf` : `Invoice-${(job?.name || 'draft').replace(/[^\w-]+/g, '-')}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  }, [jobId, invoiceId, isExistingInvoice, invoiceData, dueDate, showNote, showLineItemPricing, showLaborPricing, showAttachments, toast, job?.name]);

  const handleCancel = useCallback(() => {
    // IDI-562: an existing draft has no job route param; go back to the
    // invoice's job (its Invoices tab), as Send does, else the invoice.
    if (isExistingInvoice) {
      const backJob = job?.id;
      // IDI-566: ?tab= (not nav state) — JobDetail reads it, and it survives a reload.
      navigate(backJob ? `/jobs/${backJob}?tab=invoices` : `/invoices/${invoiceId}`);
      return;
    }
    navigate(`/jobs/${jobId}`);
  }, [jobId, invoiceId, isExistingInvoice, job?.id, navigate]);

  const toggleRecipient = useCallback((contactId) => {
    setSelectedRecipients((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  }, []);

  /* ── loading state ── */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
      </div>
    );
  }

  /* ── render ── */
  return (
    <div className="min-h-screen bg-surface-secondary">
      {/* Header */}
      <DetailPageHeader
        title="Review & Send Invoice"
        subtitle={jobHeaderLabel(job, invoiceData.jobNumber)}
        backHref={job?.id || jobId ? `/jobs/${job?.id || jobId}` : '/invoices'}
        backLabel="Back to Job"
      />

      {/* Two-column layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Live Preview */}
          <div className="lg:col-span-3">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-content-primary mb-4">
                Invoice Preview
              </h2>

              {/* GP-09: use shared DocumentHeader for branding alignment */}
              <DocumentHeader branding={branding} profile={companyProfile} docType="INVOICE" />

              {/* IDI-554: Bill To = customer, contact, street, street 2, city
                  state ZIP, contact phone (the quote's Bill To). Empty parts
                  are left out. */}
              <BillToBlock billTo={billTo} />

              {/* Invoice header info */}
              <div className="border-b border-border-default pb-4 mb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-content-secondary">Invoice Number</p>
                    <p className="text-base font-medium text-content-primary">
                      {/* IDI-53: previously fell through to `{ id: jobId }` whenever
                          no invoice exists yet (i.e. on this create screen, always) --
                          formatInvoiceNumberFor's raw-id fallback then rendered the
                          JOB's id as if it were the invoice number. An invoice that
                          doesn't exist yet has no number to show; say so instead of
                          fabricating one from an unrelated entity. */}
                      {invoiceData?.id
                        ? formatInvoiceNumberFor(invoiceData)
                        : 'Assigned on send'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-content-secondary">Amount</p>
                    <p className="text-xl font-bold text-content-primary" data-testid="invoice-amount">
                      {formatCurrency(billedAmount)}
                    </p>
                  </div>
                </div>
              </div>

              {/* IDI-481: what this job has already been invoiced, and what's left.
                  IDI-538: shown from the start ($0.00 already invoiced). */}
              {pricing && pricing.remaining != null && (
                <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-testid="invoice-remaining">
                  <span className="text-content-secondary">
                    Already invoiced <span className="font-medium text-content-primary">{formatCurrency(pricing.invoicedToDate)}</span>
                  </span>
                  <span className="text-content-secondary">
                    Remaining <span className="font-medium text-content-primary">{formatCurrency(Math.max(0, pricing.remaining))}</span>
                    {pricing.overInvoiced > 0 && <> (over-invoiced by {formatCurrency(pricing.overInvoiced)})</>}
                  </span>
                </div>
              )}
              {nothingLeft && (
                <div className="mb-4">
                  {/* Alert has no `title` prop: the heading is part of the body. */}
                  <Alert variant="warning">
                    <strong>Nothing left to bill.</strong>{' '}
                    This job has been fully invoiced{pricing.overInvoiced > 0 ? ` (over-invoiced by ${formatCurrency(pricing.overInvoiced)})` : ''}.
                    {' '}Edit or delete a draft invoice to change what's billed.
                  </Alert>
                </div>
              )}

              {/* IDI-450: if the server couldn't price this, say so — never
                  show a job-only total that would under-bill. */}
              {pricingError && (
                <div className="mb-4">
                  <Alert variant="error">
                    <strong>Could not price this invoice.</strong>{' '}
                    {pricingError} Reload before sending.
                  </Alert>
                </div>
              )}

              {/* PT-26 / IDI-451: split preview at the chosen ratio */}
              {isSplit && splitInvoices && (
                <div className="mb-4 p-3 rounded-md border border-border-default bg-surface-secondary">
                  <p className="text-xs font-semibold uppercase tracking-wide text-content-secondary mb-2">
                    {`${splitInvoices[0].label.match(/\(([^)]+)\)/)?.[1] || ''} / ${splitInvoices[1].label.match(/\(([^)]+)\)/)?.[1] || ''} Milestone Split`}
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-primary">{splitInvoices[0].label}</span>
                      <span className="text-sm font-medium text-content-primary">{formatCurrency(splitInvoices[0].amount)}</span>
                    </div>
                    <p className="text-xs text-content-secondary ml-2">Due: {formatDate(splitInvoices[0].dueDate) || 'On invoice date'}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-content-primary">{splitInvoices[1].label}</span>
                      <span className="text-sm font-medium text-content-primary">{formatCurrency(splitInvoices[1].amount)}</span>
                    </div>
                    <p className="text-xs text-content-secondary ml-2">Due: On completion</p>
                  </div>
                </div>
              )}

              {/* Dates & Terms */}
              <div className="grid grid-cols-3 gap-4 mb-4 pb-4 border-b border-border-default">
                <div>
                  <p className="text-xs text-content-secondary uppercase tracking-wide">Invoiced</p>
                  <p className="text-sm font-medium text-content-primary" data-testid="invoice-invoiced-date">
                    {formatDate(invoiceData.invoicedDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-content-secondary uppercase tracking-wide">Terms</p>
                  <p className="text-sm font-medium text-content-primary" data-testid="invoice-terms">{isSplit ? DUE_ON_RECEIPT.name : termsLabel}</p>
                </div>
                <div>
                  <p className="text-xs text-content-secondary uppercase tracking-wide">Due</p>
                  {/* IDI-817 D6: a split sends its deposit now, due on receipt
                      (split_create.go), so TERMS and DUE are the deposit's. */}
                  <p className="text-sm font-medium text-content-primary" data-testid="invoice-due-date">{formatDate(isSplit ? invoiceData.invoicedDate : dueDate)}</p>
                </div>
              </div>

              {/* Line Items (if toggled on) */}
              {showLineItemPricing && lineItems.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-content-primary mb-2">Line Items</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-default">
                        <th className="text-left py-1 text-content-secondary font-normal">Item</th>
                        <th className="text-right py-1 text-content-secondary font-normal">Qty</th>
                        <th className="text-right py-1 text-content-secondary font-normal">Rate</th>
                        <th className="text-right py-1 text-content-secondary font-normal">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={item.id || idx} className="border-b border-border-subtle">
                          <td className="py-1 text-content-primary">{item.name || item.description}</td>
                          <td className="py-1 text-right text-content-secondary">{item.qty || 1}</td>
                          <td className="py-1 text-right text-content-secondary">
                            {formatCurrency(item.rate ?? 0)}
                          </td>
                          <td className="py-1 text-right text-content-primary font-medium">
                            {formatCurrency(item.total || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* IDI-516 (2): Subtotal, Discount and Tax rows, as the PDF has,
                  so the page adds up to the Amount. A split shows the share
                  it bills, which is why its lines don't sum to it (item 1). */}
              {pricing && (
                <dl className="mb-4 ml-auto max-w-xs space-y-1 text-sm" data-testid="invoice-totals">
                  <div className="flex justify-between"><dt className="text-content-secondary">Subtotal</dt><dd className="tabular-nums">{formatCurrency(pricing.subtotal)}</dd></div>
                  {pricing.discount > 0 && (
                    <div className="flex justify-between"><dt className="text-content-secondary">Discount</dt><dd className="tabular-nums">-{formatCurrency(pricing.discount)}</dd></div>
                  )}
                  <div className="flex justify-between"><dt className="text-content-secondary">Tax</dt><dd className="tabular-nums">{formatCurrency(pricing.tax)}</dd></div>
                  <div className="flex justify-between font-semibold"><dt>{isSplit ? 'Job total' : 'Total'}</dt><dd className="tabular-nums">{formatCurrency(pricing.total)}</dd></div>
                  {isSplit && splitInvoices && (
                    <div className="flex justify-between font-semibold" data-testid="invoice-share">
                      <dt>{splitInvoices[0].label} billed now</dt><dd className="tabular-nums">{formatCurrency(splitInvoices[0].amount)}</dd>
                    </div>
                  )}
                </dl>
              )}

              {/* Note (if toggled on) */}
              {showNote && invoiceData.note && (
                <div className="mb-4 p-3 bg-surface-secondary rounded border border-border-subtle">
                  <p className="text-xs text-content-secondary uppercase tracking-wide mb-1">Note</p>
                  <p className="text-sm text-content-primary whitespace-pre-wrap">{invoiceData.note}</p>
                </div>
              )}

              {/* Attachments (if toggled on) */}
              {showAttachments && attachments.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-content-secondary uppercase tracking-wide mb-1">Attachments</p>
                  <ul className="space-y-1">
                    {attachments.map((a) => (
                      <li key={a.attachmentId} className="text-sm text-content-primary flex items-center gap-2">
                        <PaperClipIcon className="w-4 h-4 text-content-secondary" />
                        {a.filename}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </div>

          {/* Right: Controls */}
          <div className="lg:col-span-2 space-y-4">
            {/* Recipients */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-content-primary mb-3">Recipients</h3>
              <div className="space-y-2">
                {recipients.map((contact) => (
                  <label
                    key={contact.id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-surface-secondary cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(contact.id)}
                      onChange={() => toggleRecipient(contact.id)}
                      className="h-4 w-4 rounded border-border-default text-brand-primary focus:ring-brand-primary"
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-content-primary truncate">{contact.name}</p>
                      <p className="text-xs text-content-secondary truncate">{contact.email}</p>
                    </div>
                    {contact.primary && (
                      <span className="text-xs bg-surface-secondary text-content-secondary px-2 py-0.5 rounded">
                        Primary
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {/* IDI-485: a typed address (not a contact) can be added too. */}
              <div className="mt-3 flex gap-2">
                <input
                  type="email"
                  aria-label="Add recipient email"
                  placeholder="Add an email address"
                  value={typedEmail}
                  onChange={(e) => { setTypedEmail(e.target.value); setTypedError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTypedRecipient(); } }}
                  className="input flex-1 min-w-0 text-sm"
                  disabled={sending}
                />
                <Button variant="secondary" size="sm" onClick={addTypedRecipient} disabled={sending || !typedEmail.trim()}>Add</Button>
              </div>
              {typedError && <p className="mt-1 text-xs" role="alert" style={{ color: 'var(--color-error)' }}>{typedError}</p>}
            </Card>

            {sendResult && (
              <Card className="p-4">
                <DeliveryList invoice={sendResult} title="Last send" />
              </Card>
            )}

            {/* Display Toggles */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-content-primary mb-3">Display Options</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-content-primary">Line-item pricing</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showLineItemPricing}
                    onClick={() => setShowLineItemPricing((v) => !v)}
                    className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: showLineItemPricing ? 'var(--color-action-primary)' : 'var(--color-border)' }}
                  >
                    <span
                      className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                      style={{ transform: showLineItemPricing ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-content-primary">Labor pricing</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showLaborPricing}
                    onClick={() => setShowLaborPricing((v) => !v)}
                    className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: showLaborPricing ? 'var(--color-action-primary)' : 'var(--color-border)' }}
                  >
                    <span
                      className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                      style={{ transform: showLaborPricing ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-content-primary">Include note</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showNote}
                    onClick={() => setShowNote((v) => !v)}
                    className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: showNote ? 'var(--color-action-primary)' : 'var(--color-border)' }}
                  >
                    <span
                      className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                      style={{ transform: showNote ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-content-primary">Include attachments</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={showAttachments}
                    onClick={() => setShowAttachments((v) => !v)}
                    className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{ backgroundColor: showAttachments ? 'var(--color-action-primary)' : 'var(--color-border)' }}
                  >
                    <span
                      className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                      style={{ transform: showAttachments ? 'translateX(16px)' : 'translateX(0)' }}
                    />
                  </button>
                </label>
              </div>
            </Card>

            {/* IDI-451 (2) / D-17: payment terms are editable. With a split,
                these are the COMPLETION's terms (the deposit is due on receipt). */}
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-content-primary mb-2">
                {isSplit ? 'Completion payment terms' : 'Payment Terms'}
              </h3>
              <SelectMenu
                value={termValueFor(invoiceData.terms, paymentTermsOptions)}
                onChange={(v) => setInvoiceData((prev) => ({ ...prev, terms: termStoredName(v, paymentTermsOptions) }))}
                options={paymentTermsOptions.map((o) => ({ value: String(o.value ?? o.id), label: o.label || o.name || String(o.value ?? o.id) }))}
                aria-label="Payment terms"
              />
              {!dueDate && invoiceData.terms && (
                <p className="text-xs text-content-secondary mt-1">This term has no set due date.</p>
              )}
            </Card>

            {/* IDI-451: split into deposit + completion at any ratio, under
                any payment term. IDI-817 D4: a new invoice's only; a saved
                draft is sent whole. */}
            {!isExistingInvoice && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-content-primary mb-2">Billing Schedule</h3>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={isSplit}
                  disabled={invoiceData.terms === '50_50'}
                  onChange={(checked) => {
                    setSplitEnabled(checked);
                    if (checked) setDepositPercent(50);
                  }}
                />
                <span className="text-sm text-content-primary">Split into deposit + completion</span>
              </label>
              {invoiceData.terms === '50_50' && (
                <p className="text-xs text-content-secondary mt-1">
                  The 50/50 payment term always splits.
                </p>
              )}
              {isSplit && (
                <div className="mt-3">
                  <label htmlFor="deposit-percent" className="block text-xs text-content-secondary uppercase tracking-wide mb-1">
                    Deposit %
                  </label>
                  <input
                    id="deposit-percent"
                    type="text"
                    inputMode="decimal"
                    value={depositText}
                    disabled={invoiceData.terms === '50_50'}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.]/g, '');
                      setDepositText(raw);
                      const n = Number(raw);
                      if (raw !== '' && Number.isFinite(n) && n >= 1 && n <= 99) setDepositPercent(n);
                    }}
                    onBlur={() => {
                      const n = Number(depositText);
                      const v = Number.isFinite(n) && depositText !== '' ? Math.min(Math.max(n, 1), 99) : depositPercent;
                      setDepositPercent(v);
                      setDepositText(String(v));
                    }}
                    className="input w-24"
                  />
                  <p className="text-xs text-content-secondary mt-1">
                    Completion is billed at {Math.round((100 - depositPercent) * 10) / 10}%. The deposit is due on receipt; the completion is saved as a draft and billed for what's left when you send it.
                  </p>
                </div>
              )}
            </Card>
            )}

            {/* Note input */}
            {showNote && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-content-primary mb-2">Invoice Note</h3>
                <textarea
                  value={invoiceData.note}
                  onChange={(e) => setInvoiceData((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="Add a note that will appear on the invoice…"
                  rows={3}
                  className="w-full rounded-md border border-border-default bg-surface-primary px-3 py-2 text-sm text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent resize-none"
                />
              </Card>
            )}

            {/* Attachments */}
            {showAttachments && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold text-content-primary mb-2">Attachments</h3>
                {attachments.length === 0 ? (
                  <p className="text-sm text-content-secondary">No attachments added</p>
                ) : (
                  <ul className="space-y-1 mb-2">
                    {attachments.map((a) => (
                      <li key={a.attachmentId} className="text-sm text-content-primary flex items-center justify-between">
                        <span className="flex items-center gap-1"><PaperClipIcon className="h-3.5 w-3.5" /> {a.filename}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(a)}
                          className="text-xs text-content-secondary hover:text-danger-default"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-content-secondary mb-2">Internal: kept on the job, not sent to the customer.</p>
                <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)} disabled={!jobIdForFiles} title="Attach a file to this invoice">
                  + Add Attachment
                </Button>
              </Card>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-3 border-t border-border-default pt-4">
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleDownloadPDF} disabled={downloading}>
            {downloading ? 'Generating…' : 'Download PDF'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={sending || selectedRecipients.length === 0 || nothingLeft}
            title={nothingLeft ? 'Nothing is left to bill on this job' : (selectedRecipients.length === 0 ? 'Add at least one recipient' : undefined)}
          >
            {sending ? 'Sending…' : 'Send Invoice'}
          </Button>
        </div>
      </div>

      {/* §2 (2026-08-08): invoice-scope attach modal (B2 enabled). */}
      {/* IDI-484 (Q-45/Q-46): an invoice's files are stored on its JOB, tagged
        * with this invoice (origin). A new invoice has no id yet: the file is
        * tagged on create. Internal only, never sent to the customer. */}
      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="job"
        ownerId={jobIdForFiles}
        originType="invoice"
        originId={invoiceId || undefined}
        ownerLabel={`Invoice ${formatInvoiceNumberFor(invoiceData) || invoiceId?.slice(0, 8)}`}
        onAttached={(ref) => {
          setAttachments((prev) => [...prev, ref]);
          setAttachOpen(false);
        }}
      />

      {/* IDI-485: the send composer is the confirm. */}
      <SendComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onConfirm={(composer) => doSend(composer)}
        title={isSplit ? 'Send Deposit Invoice' : 'Send Invoice'}
        // Review fix 1: only a REAL number goes in the default (a new invoice
        // has none until it's created: "Invoice from <Dealer>", never "—").
        // The send adds the real number once created (withInvoiceNumber).
        defaultSubject={defaultInvoiceSubject(invoiceData.invoiceNumber || null, dealerName)}
        subjectHint={invoiceData.invoiceNumber ? undefined : 'The invoice number is added after "Invoice" when the invoice is created.'}
        recipientsLabel={composerTo}
        recipientCount={sendRecipients(recipients, selectedRecipients).length}
        note={isSplit ? 'The deposit invoice is emailed now; the completion invoice is saved as a draft.' : undefined}
        salespersons={ccQuote?.salespersons}
        salespersonIds={ccQuote?.salesperson_ids}
        assigneeEmail={ccQuote?.assigneeEmail}
        sending={sending}
      />
    </div>
  );
}
